import { BaseButton } from '@/components/common/base-button';
import { useAccessibilityTTS } from '@/hooks';
import clsx from 'clsx';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NumberKeypad from '../number-keypad/index';
import styles from './styles.module.scss';

interface NumberSearchPadProps {
    screenMode?: 'fullscreen' | 'split';
    onSearch?: (query: string) => void;
    onClearAll?: () => void;
    onBackspace?: () => void;
    onSearchComplete?: () => void;
}

export default function NumberSearchPad({ screenMode = 'fullscreen', onSearch, onClearAll, onBackspace, onSearchComplete }: NumberSearchPadProps) {
    const [inputValue, setInputValue] = useState('');
    const { t } = useTranslation();
    const { speak } = useAccessibilityTTS();

    // Helper function to add/remove active class directly to DOM element
    const addActiveClass = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        const target = event.currentTarget;
        target.classList.add(styles['searchButton__active']);

        const timeout = setTimeout(() => {
            target.classList.remove(styles['searchButton__active']);
        }, 200);

        return () => clearTimeout(timeout);
    }, []);

    const handleNumberClick = (num: string) => {
        if (inputValue.length < 4) {
            setInputValue((prev) => prev + num);
        }
    };

    const handleClearAll = () => {
        setInputValue('');
        if (onClearAll) {
            onClearAll();
        }
    };

    const handleBackspace = () => {
        setInputValue((prev) => prev.slice(0, -1));
        if (onBackspace) {
            onBackspace();
        }
    };

    const handleSearch = () => {
        if (inputValue.trim() && inputValue.length === 4) {
            // Call the parent onSearch if provided
            if (onSearch) {
                onSearch(inputValue);
            }

            // Trigger focus callback after search
            if (onSearchComplete) {
                // Use setTimeout to ensure DOM is updated after search
                setTimeout(() => {
                    onSearchComplete();
                }, 50);
            }
        }
    };

    const formatDisplayValue = () => {
        // Create an array of 4 elements, initially filled with '0'
        const displayArray = ['0', '0', '0', '0'];

        // Replace zeros from left to right with input values
        for (let i = 0; i < inputValue.length && i < 4; i++) {
            displayArray[i] = inputValue[i];
        }

        // Join with spaces for display
        return displayArray.join(' ');
    };

    return (
        <div
            className={`fullscreen:widescreen:gap-[calc(40*var(--vh-unit))] fullscreen:gap-[calc(52*var(--vh-unit))] flex flex-col items-center gap-[calc(24*var(--vh-unit))]`}
        >
            {/* Input and Instructions Group */}
            <div className="flex w-[calc(294*var(--vw-unit))] flex-col items-center gap-[calc(20*var(--vh-unit))]">
                {/* Input Display */}
                <div
                    data-region-nav
                    data-region-order={4}
                    tabIndex={4}
                    data-interactive
                    aria-label={inputValue ? `차량번호 읽기 전용 편집창 ${inputValue}` : '차량번호 읽기 전용 편집창'}
                    className="focus-border flex h-[calc(102*var(--vh-unit))] w-[calc(294*var(--vw-unit))] items-center justify-center rounded-[calc(12*var(--vh-unit))] border-[calc(2*var(--vh-unit))] border-[color:var(--gray-cc,#CCC)] bg-[color:var(--color-bg-white)] px-[calc(16*var(--vw-unit))] py-[calc(8*var(--vh-unit))]"
                >
                    {inputValue ? (
                        <div className="flex items-center gap-[calc(16*var(--vh-unit))] text-[calc(60*var(--vh-unit))] leading-none font-semibold">
                            {formatDisplayValue()
                                .split(' ')
                                .map((char, index) => (
                                    <span
                                        key={index}
                                        className={clsx(
                                            index < inputValue.length ? 'text-[var(--color-text-main01)]' : 'text-[var(--color-text-sub01)]',
                                            'number'
                                        )}
                                    >
                                        {char}
                                    </span>
                                ))}
                        </div>
                    ) : (
                        <span className="number flex items-center text-[calc(60*var(--vh-unit))] leading-none font-semibold text-[var(--color-text-sub01)]">
                            0 0 0 0
                        </span>
                    )}
                </div>
                {/* Instructions */}
                {screenMode === 'fullscreen' && (
                    <p
                        tabIndex={5}
                        data-region-order={5}
                        data-interactive
                        data-region-nav
                        aria-label={t('parking.numberPad.ariaLabel')}
                        className="focus-border text-center text-[calc(20*var(--vh-unit))] leading-[calc(28*var(--vh-unit))] font-medium tracking-[calc(-0.4*var(--vh-unit))] text-[var(--color-text-main01)]"
                    >
                        {t('parking.numberPad.instructionsBefore')}
                        <br />
                        {t('parking.numberPad.instructionsAfter')}
                    </p>
                )}
            </div>

            {/* Keypad and Search Button Group */}
            <div className={`fullscreen:gap-[calc(40*var(--vh-unit))] flex flex-col items-center gap-[calc(24*var(--vh-unit))]`}>
                {/* Number Keypad */}
                <NumberKeypad onNumberClick={handleNumberClick} onClearAll={handleClearAll} onBackspace={handleBackspace} currentInput={inputValue} />
                {/* Search Button */}
                <BaseButton
                    data-tts-scope="parking-search-button"
                    aria-disabled={inputValue.length !== 4}
                    data-region-nav
                    data-region-order={8}
                    tabIndex={8}
                    data-interactive
                    role="button"
                    className={styles.searchButton}
                    onClick={(event) => {
                        if (inputValue.length !== 4) {
                            speak(`${t('parking.searchButton.ariaLabel')} ${t('common.button.tts.inactive_button')}`);
                        } else {
                            speak(`${t('parking.searchButton.ariaLabel')} ${t('common.button.tts.button')}`);
                        }
                        addActiveClass(event);
                        handleSearch();
                    }}
                    onFocus={() => {
                        if (inputValue.length !== 4) {
                            speak(`${t('parking.searchButton.ariaLabel')} ${t('common.button.tts.inactive_button')}`);
                        } else {
                            speak(`${t('parking.searchButton.ariaLabel')} ${t('common.button.tts.button')}`);
                        }
                    }}
                >
                    {t('parking.searchButton.text')}
                </BaseButton>
            </div>
        </div>
    );
}
